import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { MessageCircle, Check } from "lucide-react";

// Looping fake DM that shows the product in 4 beats:
//   0 empty -> 1 buyer asks -> 2 typing dots -> 3 reply + pay link -> 4 paid
// then resets. Pure client-side, no network. With "reduce motion" on, it
// just shows the final (paid) state without looping.
const HOLD_MS = [600, 1300, 1100, 1700, 2800]; // how long each step stays

const bubble = {
  initial: { opacity: 0, y: 10, scale: 0.96 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

export default function HeroChatDemo() {
  const reduce = useReducedMotion();
  const [step, setStep] = useState(reduce ? 4 : 0);

  useEffect(() => {
    if (reduce) {
      setStep(4);
      return undefined;
    }
    const t = setTimeout(() => setStep((s) => (s + 1) % HOLD_MS.length), HOLD_MS[step]);
    return () => clearTimeout(t);
  }, [step, reduce]);

  return (
    <>
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink/60">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-hotpink opacity-60" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-hotpink" />
        </span>
        Example checkout
      </p>

      {/* fixed height so the card never jumps while bubbles come and go */}
      <div className="mt-3 flex h-[132px] flex-col justify-end space-y-2 overflow-hidden">
        <AnimatePresence mode="popLayout">
          {step >= 1 && (
            <motion.div
              key="buyer"
              {...bubble}
              className="w-fit max-w-[85%] rounded-xl rounded-bl-sm bg-ink/5 px-3 py-2 text-xs text-ink/80"
            >
              Hey! Is this available in size M? 😍
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="typing"
              {...bubble}
              className="ml-auto flex w-fit items-center gap-1 rounded-xl rounded-br-sm bg-sunset-gradient px-3 py-2.5"
            >
              {[0, 1, 2].map((i) => (
                <motion.span
                  key={i}
                  className="h-1.5 w-1.5 rounded-full bg-white"
                  animate={{ y: [0, -3, 0], opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.15 }}
                />
              ))}
            </motion.div>
          )}

          {step >= 3 && (
            <motion.div
              key="reply"
              {...bubble}
              className="ml-auto flex w-fit items-center gap-2 rounded-xl rounded-br-sm bg-sunset-gradient px-3 py-2 text-xs text-white"
            >
              <MessageCircle size={14} className="flex-none" />
              Yes! Grab it here → Pay ₹1,299
            </motion.div>
          )}

          {step >= 4 && (
            <motion.div
              key="paid"
              {...bubble}
              className="flex w-fit items-center gap-1.5 rounded-xl bg-mint px-3 py-2 text-xs font-bold text-ink"
            >
              <Check size={14} strokeWidth={3} className="flex-none" />
              Payment received · ₹1,299
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
